import {useEffect} from "react";
import {Grid, Typography, useMediaQuery} from "@mui/material";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import {useStyles} from "../../utils/uiUtils";


/**
 * WidgetsGrid component renders a grid of widgets with titles.
 *
 * On a wide screen every widget is placed on ONE row, whatever the count — 3 widgets take 4 columns
 * each, 4 take 3. It used to be a fixed 6 columns, so any more than two widgets wrapped, and each extra
 * row cost another widget's height of scrolling. Narrower charts on one row beat taller charts the
 * user has to scroll to reach; below the wide breakpoint they go full-width and stack as before, which
 * is the only thing that works on a phone.
 *
 * Height comes from the width by aspect ratio unless the caller passes `fillHeight`. That is deliberate
 * and was tried the other way round: capping the height to a slice of the viewport shrank the donut
 * charts until their centre totals spilled over the ring and the legend below the chart was cut off
 * entirely — a chart small enough to fit is not a chart worth fitting. So a widget with something to
 * draw takes the room it needs and the page scrolls to it.
 *
 * @param {Element[]} widgets - Array of widget components to be displayed in the grid.
 * @param {string[]} titles - Array of titles corresponding to each widget.
 * @param {string[]} [variants] - Array of variants for each widget. If not needed, provide null or undefined.
 * @param {Object[]} [widgetProps] - Array of props for each widget. If not needed, provide null or undefined.
 * @param {boolean} [fillHeight] - Take the height of the parent instead of deriving it from the width.
 *        For a parent that has already been given a height by something else — the dashboard puts one
 *        graph beside the Media Metrics card, where the counters set the height and the graph has to
 *        match it or the two will not line up along the bottom. Ignored below the wide breakpoint, where
 *        widgets stack one per row and there is no neighbour to match.
 * @param {boolean[]} [hidden] - Widgets to leave off the screen, by position. Hidden rather than not rendered:
 *        the dashboard hides a graph that has nothing to draw, and a graph that is not mounted cannot find out
 *        that a change of job has given it something. The visible ones share the row between them.
 * @returns {Element}
 * @constructor
 */
export const WidgetsGrid = ({widgets, titles, variants, widgetProps, fillHeight = false, hidden = []}) => {
    useEffect(() => {
        if (!Array.isArray(widgets)) throw new Error("widgets must be an array");
        if (!Array.isArray(titles)) throw new Error("titles must be an array");
        if (widgets.length !== titles.length) throw new Error("widgets and titles must have the same length");
    }, [widgets, titles]);

    const classes = useStyles();

    const isWide = useMediaQuery('(min-width:800px)');

    // 12 / n, floored, so every widget lands on one row: 3 → 4 columns each, 4 → 3. Guarded at 1 for
    // more than twelve widgets, which no caller does today but which would otherwise ask for a zero-width
    // column and render nothing at all.
    const visibleCount = Math.max(1, widgets.filter((_, idx) => !hidden[idx]).length);
    const columnSpan = isWide ? Math.max(1, Math.floor(12 / visibleCount)) : 12;

    // Only meaningful on a wide screen: stacked, the widgets need their own heights.
    const stretchToParent = fillHeight && isWide;

    return (
        <Grid
            container
            columnSpacing={1.5}
            /*
             * Row spacing is dropped when filling a parent, and this is not cosmetic: MUI implements
             * spacing as a negative margin on the container plus matching padding on every item, so a
             * Paper at `height: 100%` ends up half a spacing unit short at the top and again at the
             * bottom. That was the visible gap between this widget's lower edge and the Media Metrics
             * card beside it — 24px of grid gutter, not a misjudged height.
             *
             * Column spacing is untouched, since it separates widgets from each other rather than from
             * the box they are filling. Nothing is lost vertically either: a filling grid is one row.
             */
            rowSpacing={stretchToParent ? 0 : 1.5}
            alignItems="stretch"
            // flexGrow/minWidth are for the case the parent is itself a flex row, which is how the
            // dashboard mounts this: without them the negative margins spacing applies leave the row
            // sized to its content rather than to the space it was given.
            sx={stretchToParent ? {height: '100%', flexWrap: 'nowrap', flexGrow: 1, minWidth: 0} : undefined}
        >
            {widgets.map((WidgetComponent, idx) => (
                <Grid item
                      key={`widget_${idx}_title_${titles[idx]}`}
                      xs={columnSpan}
                      sx={{minWidth: 0, ...(hidden[idx] ? {display: 'none'} : {})}}
                >
                    <Paper
                        elevation={3}
                        className={classes.mainContainer}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            background: 'transparent',
                            // Matched to the neighbour, or else derived from the width so the chart inside
                            // keeps its proportions and its legend.
                            ...(stretchToParent ? {height: '100%'} : {aspectRatio: '810/550'}),
                            boxSizing: 'border-box',
                            overflow: 'visible',
                            padding: 1.25,
                            minWidth: 0
                        }}
                    >
                        {/* An empty title means the widget draws its own heading — Ingested Data puts the
                            period tabs above it, which cannot be done from out here. Skipped rather than
                            rendered empty, or the frame would still take the row's height and the widget's
                            own heading would sit under a blank one. */}
                        {titles[idx] ?
                            <Box pb={1}>
                                <Typography
                                    align='left'
                                    className={classes.mainHeading}
                                    style={{
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        lineHeight: '20px'
                                    }}>{titles[idx]}</Typography>
                            </Box> :
                            <></>}
                        <Box
                            sx={{
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'visible',
                            }}>
                            <Box sx={{width: '100%', height: '100%', display: 'flex',overflow: 'visible',}}>
                                <WidgetComponent
                                    variant={variants?.[idx] ?? undefined}
                                    {...(widgetProps?.[idx] || {})}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

